import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import ContentPageLayout from "../components/ContentPageLayout";
import { GUIDES, GUIDE_CLUSTERS } from "../content/guides";
import { trackEvent } from "../utils/analytics";

const GuidesIndexPage = () => {
  useEffect(() => {
    trackEvent("guides_index_opened");
  }, []);

  return (
    <ContentPageLayout>
      <main style={styles.main}>
        <h1 style={styles.title}>Comic & Storyboard Guides</h1>
        <p style={styles.subtitle}>
          Practical, craft-focused guides to designing, lettering, and printing
          comics, and to planning shots for film and video — the ideas behind good
          pages, not just the buttons. New to the tool itself? Start with the{" "}
          <Link to="/tutorial" style={styles.inlineLink}>step-by-step tutorial</Link>.
        </p>

        {GUIDE_CLUSTERS.map((cluster) => (
          <section key={cluster.key} style={styles.cluster}>
            <h2 style={styles.clusterTitle}>{cluster.title}</h2>
            {cluster.key === "storyboards" && (
              <p style={styles.clusterIntro}>
                Pair these with the free{" "}
                <Link to="/storyboard-templates" style={styles.inlineLink}>storyboard templates</Link>.
              </p>
            )}
            <div style={styles.grid}>
              {GUIDES.filter((g) => (g.cluster || "comics") === cluster.key).map((g) => (
                <Link key={g.slug} to={`/guides/${g.slug}`} style={styles.card}>
                  <div style={styles.cardHeader}>
                    <span className="material-icons" style={styles.cardIcon}>{g.icon}</span>
                    <span style={styles.readingTime}>{g.readingTime}</span>
                  </div>
                  <h3 style={styles.cardTitle}>{g.title}</h3>
                  <p style={styles.cardSummary}>{g.summary}</p>
                  <span style={styles.readMore}>Read guide →</span>
                </Link>
              ))}
            </div>
          </section>
        ))}

        <div style={styles.ctaSection}>
          <p style={styles.ctaText}>Ready to put it into practice?</p>
          <div style={styles.ctaRow}>
            <Link to="/editor" style={styles.ctaButton}>Open the comic editor</Link>
            <Link to="/storyboard" style={styles.ctaButton}>Make a storyboard</Link>
          </div>
        </div>
      </main>
    </ContentPageLayout>
  );
};

const styles = {
  main: {
    flex: 1,
    width: "100%",
    maxWidth: 960,
    margin: "0 auto",
    padding: "48px 24px",
    boxSizing: "border-box",
  },
  title: {
    fontSize: 42,
    fontWeight: 700,
    marginBottom: 8,
    color: "#1a1a1a",
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  subtitle: {
    fontSize: 17,
    color: "#666",
    marginBottom: 40,
    maxWidth: 680,
  },
  inlineLink: { color: "#00a4e4", textDecoration: "none" },
  cluster: { marginBottom: 40 },
  clusterTitle: {
    fontSize: 16,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 1,
    color: "#1a1a1a",
    borderBottom: "2px solid #1a1a1a",
    paddingBottom: 8,
    margin: "0 0 16px",
  },
  clusterIntro: { fontSize: 15, color: "#666", margin: "-4px 0 16px" },
  ctaRow: { display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "center" },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: 20,
  },
  card: {
    display: "flex",
    flexDirection: "column",
    border: "2px solid #1a1a1a",
    background: "#fff",
    padding: "18px 20px 20px",
    textDecoration: "none",
    color: "#1a1a1a",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  cardIcon: {
    fontSize: 22,
    color: "#1a1a1a",
    background: "#ffe600",
    borderRadius: 2,
    padding: "4px 5px",
  },
  readingTime: {
    fontSize: 12,
    color: "#999",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  cardTitle: {
    fontSize: 20,
    fontWeight: 700,
    margin: "0 0 10px",
    lineHeight: 1.3,
  },
  cardSummary: {
    fontSize: 14,
    color: "#555",
    margin: "0 0 16px",
    flex: 1,
  },
  readMore: {
    fontSize: 14,
    fontWeight: 700,
    color: "#00a4e4",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  ctaSection: { textAlign: "center", padding: "48px 0 8px" },
  ctaText: { fontSize: 17, color: "#666", marginBottom: 16 },
  ctaButton: {
    display: "inline-block",
    padding: "14px 32px",
    background: "#1a1a1a",
    color: "#f5f0e8",
    fontSize: 16,
    fontWeight: 700,
    textDecoration: "none",
    textTransform: "uppercase",
    letterSpacing: 1,
    border: "2px solid #1a1a1a",
  },
};

export default GuidesIndexPage;
